import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../interceptors";
import Modal from "../shared/Modal";

// ── WhatsApp setup nudge ─────────────────────────────────────────────────────
// Mounted once globally in AppShell. On login (first mount of the shell) it
// checks whether this tenant has its WhatsApp Business config in place:
//
//   GET /job-posting/api/whatsapp/{tenantId}/config
//
// If the config is missing (404 or an empty/unconfigured response) we prompt
// the user to set it up. "Set up now" sends them to the settings page;
// "Remind me later" just closes the dialog and suppresses it for the rest of
// the browser session (it will nudge again on the next login).
//
// Transient failures (network / 5xx) are ignored so we never nag the user to
// "set up WhatsApp" when the backend is simply unreachable.

const DISMISS_KEY = "whatsappSetupPromptDismissed";
const SETTINGS_PATH = "/admin/whatsapp-settings";

const isConfigured = (data) =>
  !!data && (data.status === "CONNECTED" || !!data.phoneNumberId);

export default function WhatsAppSetupPrompt() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const tenantId = useMemo(
    () => Number(localStorage.getItem("tenantId")) || 1,
    [],
  );

  useEffect(() => {
    // Already dismissed this session — don't ask again until next login.
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "true") return undefined;
    } catch {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(
          `/job-posting/api/whatsapp/${tenantId}/config`,
        );
        if (cancelled) return;
        if (!isConfigured(data)) setOpen(true);
      } catch (e) {
        // 404 = no config row yet → prompt. Any other error (network / 5xx)
        // is treated as "unknown" and left alone.
        if (!cancelled && e?.response?.status === 404) setOpen(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const remindLater = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "true");
    } catch {
      /* sessionStorage blocked — fine, just close */
    }
    setOpen(false);
  };

  const setUpNow = () => {
    setOpen(false);
    navigate(SETTINGS_PATH);
  };

  return (
    <Modal
      open={open}
      onClose={remindLater}
      title="Set up WhatsApp Business"
      description="Connect the Meta WhatsApp Business Cloud API to message candidates."
      size="sm"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            onClick={remindLater}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              background: "white",
              color: "#475569",
              border: "1px solid #E2E8F0",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Remind me later
          </button>
          <button
            type="button"
            onClick={setUpNow}
            data-autofocus
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              background: "#25D366",
              color: "white",
              border: "none",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Set up now
          </button>
        </div>
      }
    >
      <p style={{ fontSize: 13, color: "#475569", margin: 0, lineHeight: 1.5 }}>
        Your workspace doesn’t have WhatsApp configured yet. Add your phone
        number ID, access token, and other Meta credentials to start sending and
        receiving WhatsApp messages with candidates.
      </p>
    </Modal>
  );
}
